/* CH-1 multi-term small-multiples (chart-pack spec §1 CH-1 + D6).
 *
 * The single "Has this moved?" trend answers one search at a time. This is the
 * comparison view: one compact SoV% mini-trend PER TRACKED SEARCH (term ×
 * location — the atomic unit IMPACT tracks and fetchTimeline queries), all on
 * the SAME 0–100% y-scale so markets read against each other at a glance. It
 * generalizes the persona's "several terms on one chart" to the real data
 * shape (a brand is often one term across many cities).
 *
 * HONESTY (inherited from CH-1, per Design): each mini-plot reuses
 * percentLineLayout/percentLineSvg — so it BREAKS across polling holes (never
 * interpolates), plots a single capture as one marker (never a fabricated flat
 * line), and carries its own provenance caption; a combined table-view twin
 * lists the current standing for every selected search. A search whose live
 * timeline can't be fetched falls back to its one measured snapshot capture —
 * an honest single point, labelled as such, never an empty promise.
 *
 * Pure hub: N `fetchTimeline` calls against the EXISTING /impact/timeline
 * endpoint (memoized in api/impact.ts), one per selected search. No new
 * endpoint, no new data.
 */
import { captureSerp, isSerpCurrent, serpRoot } from './mountRoot.ts';
import { html, nothing, render } from 'lit';
import { fetchTimeline } from '../api/impact.ts';
import { ownershipByTermLoc } from '../derive/search.ts';
import { fixMojibake, pct } from '../lib/format.ts';
import { chartCaption, chartEmpty, chartTableTwin, percentLineLayout, percentLineSvg } from '../ui/charts.ts';
import { pollsToSeries } from './trend.ts';
import type { AppPayloadState } from '../derive/state-shape.ts';

export interface SmUnit {
  key: string;
  term: string;
  location: string;
  label: string;
  /** null on a partial-coverage term (unmeasured) — never fabricated to 0. */
  owned: number | null;
  slots: number | null;
  negative: number | null;
  polledAt?: string;
}

interface SmState {
  /** selected tracked-search keys, or null before first init. */
  selected: Set<string> | null;
}
export const SM_STATE: SmState = { selected: null };

/** How many searches are selected by default — bounds the initial fetch fan-out
 * (each is a live /impact/timeline call); the rest are one chip-click away. */
export const SM_DEFAULT_CAP = 6;

// compact viewBox for a mini SoV% plot (§5.2 small-multiples, ~2-up). padL fits
// the "100%" y-label (anchored at padL-6) without clipping it at the edge.
const SM_OPTS = { width: 320, height: 172, padL: 36, padR: 28, plotTop: 8, plotH: 96 };

/** The tracked-search units for the small-multiples, one per term×location.
 * Pure (unit-tested): label + a stable key for selection. */
export function smUnits(state: AppPayloadState): SmUnit[] {
  return ownershipByTermLoc(state).map((r) => ({
    key: r.key,
    term: r.term,
    location: r.location,
    label: `${fixMojibake(r.term)} · ${r.location}`,
    owned: r.owned,
    slots: r.slots,
    negative: r.negative,
    polledAt: r.polledAt,
  }));
}

/** The default-selected keys: the first `cap` units (bounds the fetch fan-out).
 * Pure (unit-tested). */
export function smDefaultSelection(units: SmUnit[], cap = SM_DEFAULT_CAP): Set<string> {
  return new Set(units.slice(0, cap).map((u) => u.key));
}

export function renderSmallMultiples(state: AppPayloadState): void {
  const card = serpRoot().querySelector<HTMLElement>('#smCard');
  const chipsEl = serpRoot().querySelector<HTMLElement>('#smTerms');
  const grid = serpRoot().querySelector<HTMLElement>('#smGrid');
  const tableEl = serpRoot().querySelector<HTMLElement>('#smTable');
  if (!card || !chipsEl || !grid) return;

  const units = smUnits(state);
  // With one tracked search the "Has this moved?" card already tells the whole
  // story — the comparison view would just duplicate it. Hide, don't fake.
  if (units.length < 2) {
    card.style.display = 'none';
    return;
  }
  card.style.display = '';
  if (!SM_STATE.selected) SM_STATE.selected = smDefaultSelection(units);

  render(
    html`${units.map((u) => {
      const on = SM_STATE.selected!.has(u.key);
      return html`<button
        type="button"
        class="smchip ${on ? 'on' : ''}"
        aria-pressed=${on}
        @click=${() => {
          if (on) SM_STATE.selected!.delete(u.key);
          else SM_STATE.selected!.add(u.key);
          renderSmallMultiples(state);
        }}
      >${u.label}</button>`;
    })}`,
    chipsEl,
  );

  const chosen = units.filter((u) => SM_STATE.selected!.has(u.key));
  const win: { start?: string; end?: string } = (state.impact && state.impact.window) || {};

  if (!chosen.length) {
    render(html`<div class="chart-cap">Select at least one tracked search to compare.</div>`, grid);
  } else {
    // Cells with an EMPTY .smbody — each body is then driven solely through its
    // OWN lit render() root (loading → chart) in smLoadCell. (Rendering into a
    // body that the grid template pre-filled would leak the placeholder: the
    // pre-existing child belongs to the grid's render part, not the body's.)
    render(
      html`${chosen.map(
        (u, i) => html`<div class="smcell" data-idx=${i}>
          <h4 title="${u.label}">${u.label}</h4>
          <div class="smbody"></div>
        </div>`,
      )}`,
      grid,
    );
    chosen.forEach((u, i) => void smLoadCell(state, u, i, win));
  }

  // combined table-view twin — current standing per selected search, synchronous
  // (the snapshot is always available; the mini-plots add the trend where live
  // history exists).
  if (tableEl) {
    render(
      chosen.length
        ? chartTableTwin(
            ['Tracked search', '% owned', 'Owned / slots'],
            chosen.map((u) => u.owned != null && u.slots != null
              ? [u.label, `${pct(u.owned / Math.max(u.slots, 1))}%`, `${u.owned}/${u.slots}`]
              : [u.label, 'not measured', 'page-1 not classified']),
          )
        : html``,
      tableEl,
    );
  }
}

async function smLoadCell(state: AppPayloadState, u: SmUnit, idx: number, win: { start?: string; end?: string }): Promise<void> {
  // FIX-audit F2: scope the cell lookup to the CAPTURED render root, never the
  // global document — two mounts (legacy #serpBody + the deep-surface page) can
  // both hold a #smGrid, and document.querySelector would grab the wrong one and
  // cross-paint. Capture the context at request start; discard the commit if a
  // newer render superseded this root, or the cell detached, during the await.
  const ctx = captureSerp();
  const body = ctx.root.querySelector<HTMLElement>(`#smGrid .smcell[data-idx="${idx}"] .smbody`);
  if (!body) return;
  render(html`<div class="chart-cap">loading movement…</div>`, body); // body's own render root
  try {
    const data = await fetchTimeline({
      brand: state.brand || '',
      term: u.term,
      location: u.location,
      start: win.start || '',
      end: win.end || '',
      max_polls: '12',
    });
    const polls = data.polls || [];
    if (!data.available || !polls.length) throw new Error(data.reason || 'no polls');
    // guard against a supersede (another surface/client) or the grid having
    // re-rendered (a chip toggle) while awaiting
    if (!isSerpCurrent(ctx) || !body.isConnected) return;
    renderMini(body, pollsToSeries(polls), u, false);
  } catch {
    if (!isSerpCurrent(ctx) || !body.isConnected) return;
    // offline / no live history: the run's one measured capture is honest as a
    // single point (CH-1 plots it as one marker, never a flat line).
    renderMini(body, [{ date: String(u.polledAt || win.end || '').slice(0, 10), owned: u.owned ?? 0, negative: u.negative ?? 0, slots: u.slots ?? 0 }], u, true);
  }
}

function renderMini(
  body: HTMLElement,
  series: Array<{ date: string; owned: number; negative: number; slots: number }>,
  u: SmUnit,
  snapshot: boolean,
): void {
  const points = series
    .filter((p) => p.date)
    .map((p) => ({ date: p.date, value: (p.owned || 0) / Math.max(p.slots || 0, 1), sub: p.negative || 0 }));
  if (!points.length) {
    render(chartEmpty('No IMPACT capture for this search.'), body);
    return;
  }
  const layout = percentLineLayout(points, SM_OPTS);
  const n = points.length;
  const last = points[n - 1];
  if (!last) return; // callers never pass an empty series — narrows for TS
  const aria = `${fixMojibake(u.term)} in ${u.location}: ${pct(last.value)}% of page 1 owned${n > 1 ? ` over ${n} polled days` : ' (single capture)'}.`;
  const cap = snapshot ? 'Current capture · IMPACT' : `${n} polled ${n === 1 ? 'day' : 'days'} · IMPACT`;
  const holeNote = layout.holes.length
    ? html`<div class="chart-cap chart-holes">${layout.holes.map((h) => `no capture ${h.from.slice(5)} → ${h.to.slice(5)}`).join(' · ')}</div>`
    : nothing;
  render(html`${percentLineSvg(layout, aria)}${chartCaption(cap)}${holeNote}`, body);
}
